import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { formatPct } from "@/lib/gente-gestao/format";
import { TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";
import {
  csvCicloRespostasUrl,
  downloadGgCsv,
  downloadGgFile,
  pdfLiderUrl,
  type GgLiderancaResultadosDto,
  type GgLiderancaScorecardDto,
} from "@/services/genteGestao";

function fmtNota(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function AbaLiderancaResultados({
  resultados,
}: {
  resultados: GgLiderancaResultadosDto;
}) {
  const { cards, ranking, competencias, comentarios, ciclo } = resultados;
  const [liderId, setLiderId] = useState(ranking[0]?.liderId ?? "");
  const [busca, setBusca] = useState("");
  const selecionado: GgLiderancaScorecardDto | undefined = ranking.find((r) => r.liderId === liderId) ?? ranking[0];

  const comentariosFiltrados = useMemo(() => {
    return comentarios.filter((c) => {
      if (liderId && c.liderId !== liderId) return false;
      if (busca && !c.texto.toLowerCase().includes(busca.toLowerCase())) return false;
      return true;
    });
  }, [comentarios, liderId, busca]);

  const chartData = (selecionado && !selecionado.oculto ? selecionado.competencias : competencias).map((c) => ({
    nome: c.competenciaNome.length > 18 ? `${c.competenciaNome.slice(0, 16)}…` : c.competenciaNome,
    media: c.media ?? 0,
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void downloadGgCsv(csvCicloRespostasUrl(ciclo.id), `avaliacoes-${ciclo.id}.csv`)}
        >
          Exportar CSV
        </Button>
        {selecionado && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              void downloadGgFile(
                pdfLiderUrl(ciclo.id, selecionado.liderId),
                `avaliacao-${selecionado.liderUsuario}.pdf`,
                "application/pdf",
              )
            }
          >
            PDF deste líder
          </Button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div
          className="rounded-xl border border-slate-200 bg-white shadow-card"
          style={{ borderLeftWidth: 4, borderLeftColor: cards.zona?.cor ?? "#94a3b8" }}
        >
          <Card
            title="Nota média visível"
            value={fmtNota(cards.notaMedia)}
            subtitle={cards.zona ? cards.zona.label : "Sem recorte visível"}
          />
        </div>
        <Card title="Respondentes" value={String(cards.respondentes)} />
        <Card title="Adesão" value={formatPct(cards.adesao, 0)} subtitle={`${cards.convidados} pares`} />
        <Card
          title="Líderes com nota"
          value={`${cards.lideresVisiveis}/${cards.lideresAvaliados}`}
          subtitle={`mín. ${ciclo.minRespondentes} respostas se confidencial`}
        />
      </div>

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Líder</th>
              <th className="px-4 py-3">Setor</th>
              <th className="px-4 py-3">Respostas</th>
              <th className="px-4 py-3">Nota</th>
              <th className="px-4 py-3">Zona</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((r) => (
              <tr
                key={r.liderId}
                className={`cursor-pointer border-t border-slate-100 ${r.liderId === selecionado?.liderId ? "bg-brand-50" : ""}`}
                onClick={() => setLiderId(r.liderId)}
              >
                <td className="px-4 py-2 font-medium">{r.liderNome}</td>
                <td className="px-4 py-2">{r.setor ?? "—"}</td>
                <td className="px-4 py-2">
                  {r.respondentes}/{r.convidados}
                </td>
                <td className="px-4 py-2 font-semibold">{r.oculto ? "—" : fmtNota(r.notaGeral)}</td>
                <td className="px-4 py-2">
                  {r.oculto ? (
                    <span className="text-xs text-slate-500">{r.motivo}</span>
                  ) : (
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-semibold text-white"
                      style={{ background: r.zona?.cor ?? "#94a3b8" }}
                    >
                      {r.zona?.label ?? "—"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ChartContainer
        title={selecionado && !selecionado.oculto ? `Competências — ${selecionado.liderNome}` : "Competências (líderes visíveis)"}
        height={260}
        empty={chartData.length === 0}
      >
        <BarChart data={chartData} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="nome" interval={0} angle={-20} textAnchor="end" height={70} />
          <YAxis domain={[0, 5]} />
          <Tooltip cursor={TOOLTIP_CURSOR_BAND} />
          <Bar dataKey="media" name="Média" fill="#0f766e" />
        </BarChart>
      </ChartContainer>

      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="Filtrar comentários por líder"
          value={liderId}
          onChange={(e) => setLiderId(e.target.value)}
          options={ranking.map((r) => ({ value: r.liderId, label: r.liderNome }))}
          placeholder="Todos"
        />
        <Input label="Buscar no texto" value={busca} onChange={(e) => setBusca(e.target.value)} />
      </div>
      <ul className="flex flex-col gap-2">
        {comentariosFiltrados.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum comentário neste recorte.</p>
        ) : (
          comentariosFiltrados.map((c, i) => (
            <li key={`${c.perguntaId}-${i}`} className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
              <p className="text-xs text-slate-500">
                {c.liderNome} · {c.perguntaTexto}
                {c.colaboradorNome ? ` · ${c.colaboradorNome}` : ""}
              </p>
              <p className="mt-1 text-slate-800">{c.texto}</p>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
